<script setup>
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { LMap, LTileLayer } from '@vue-leaflet/vue-leaflet'

import dealershipIcon from '../../../assets/icons/dealership-icon.svg'
import serviceCenterIcon from '../../../assets/icons/service-center-icon.svg'
import mobileMechanicIcon from '../../../assets/icons/mobile-mechanic-icon.svg'

import { FontAwesomeIcon, FontAwesomeLayers } from '@fortawesome/vue-fontawesome'
import { faCheck, faHeart, faPhone } from '@fortawesome/free-solid-svg-icons'
import { faCircle } from '@fortawesome/free-regular-svg-icons'
import Rating from '@/components/ui/rating.vue'

const props = defineProps({
  allQuotePrices: {
    type: Array,
    required: true
  },
  userQuotePrice: {
    type: Object,
    required: true
  },
  quote: {
    type: Object,
    required: true
  }
})

// I18n
const { t } = useI18n()
const currencyFormater = Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

// Variables
// Not ideal, the questions are dynamic, but we need to know the answer to this specific question
const outTheDoorQuestionId = 43
const oemPartsQuestionId = 5

const isLoading = ref(true)
const mapCenter = computed(() => {
  return [props.userQuotePrice.service_provider.latitude, props.userQuotePrice.service_provider.longitude]
})

onMounted(async () => {
})

const isBestPrice = () => {
  const lowest_price = Math.min(...(props.allQuotePrices.map((quote_price) => Number(quote_price.price.price))))
  return Number(props.userQuotePrice.price.price) === lowest_price
}

function question_answer(question_id) {
  return props.userQuotePrice.price.price_question_answers.find(x => x.question_id === question_id)?.answer
}

</script>

<template>
  <div class="rounded-xl border border-black bg-white w-full mb-8">
    <div class="relative flex flex-col w-full md:min-h-64 min-h-96">
      <div class="absolute -top-4 left-0 h-8 w-full flex justify-center items-center" v-if="isBestPrice()">
        <p class="w-[150px] rounded-xl border border-black bg-white text-center text-lg">
          {{ t('my_garage.quotes.best_price') }}
        </p>
      </div>

      <div
        class="flex flex-col md:flex-row items-stretch mt-2 absolute top-0 bottom-0 left-0 right-0 px-2 md:px-8 py-4">
        <div class="h-[100%] aspect-square rounded-xl border bg-gray-100 overflow-hidden">
          <l-map ref="map" :zoom="16" :minZoom="16" :maxZoom="16" :center="mapCenter" :use-global-leaflet="false"
                 :options="{ zoomControl: false, dragging: false, attributionControl:false, scrollWheelZoom: false }">
            <l-tile-layer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          layer-type="base" name="OpenStreetMap"></l-tile-layer>
          </l-map>
        </div>

        <div class="grow px-4 flex flex-col h-full">
          <div class="flex items-baseline gap-2 text-xs md:text-base">
            <p class="uppercase">{{ t('my_garage.quotes.within') }}</p>
            <p class="text-xs md:text-lg font-semibold whitespace-nowrap">
              {{ t('my_garage.quotes.distance_to_provider', { distance: props.userQuotePrice.distance_to_provider }) }}
            </p>

            <div class="flex items-baseline" v-if="props.userQuotePrice.service_provider.is_dealership">
              <img class="h-4 md:h-6 pl-2" :src="dealershipIcon" alt="" />
              <p class="uppercase">{{ t('my_garage.quotes.service_chain_dealership') }}</p>
            </div>

            <div class="flex items-baseline" v-if="props.userQuotePrice.service_provider.is_chain">
              <img class="h-4 md:h-6 pl-2" :src="serviceCenterIcon" alt="" />
              <p class="uppercase">{{ t('my_garage.quotes.service_chain_chain') }}</p>
            </div>

            <div class="flex items-baseline" v-if="props.userQuotePrice.service_provider.is_independent">
              <img class="h-4 md:h-6 pl-2" :src="serviceCenterIcon" alt="" />
              <p class="uppercase">{{ t('my_garage.quotes.service_chain_independent') }}</p>
            </div>
          </div>

          <div class="flex items-center gap-2 mb-2 md:mb-6">
            <h3 class="text-lg md:text-2xl font-semibold">
              {{ props.userQuotePrice.service_provider.name }}
            </h3>
          </div>

          <p>{{ props.userQuotePrice.service_provider.address }}</p>

          <div class="flex items-baseline" v-if="false">
            <!-- TODO: Add information to service provider this doesn't exist yet -->
            <rating :value="3.6" :count="140"></rating>

            <span class="w-4"></span>

            <FontAwesomeLayers class="fa-lg ml-2" title="Verified">
              <FontAwesomeIcon :icon="faCircle" transform="down-2" />
              <FontAwesomeIcon :icon="faCheck" transform="shrink-6 down-2" />
            </FontAwesomeLayers>

            <FontAwesomeLayers class="fa-lg ml-2" title="Favorite">
              <FontAwesomeIcon :icon="faCircle" transform="down-2" />
              <FontAwesomeIcon :icon="faHeart" transform="shrink-6 down-2" />
            </FontAwesomeLayers>
          </div>

          <div class="grow"></div>

          <div class="flex flex-row">
            <RouterLink
              :to="{
                name: 'service_provider.profile',
                params: {
                  id: props.userQuotePrice.service_provider.id
                },
                query: {
                  lat: props.quote.user_address.latitude,
                  lng: props.quote.user_address.longitude
                }
              }"
              class="rounded-full bg-[#b05b34] text-white py-2 md:py-3 px-8 hover:opacity-90">
              <span class="focus:outline-none text-sm font-normal">
                {{ t('my_garage.quotes.more_info') }}
              </span>
            </RouterLink>
          </div>
        </div>

        <div class="flex flex-col">
          <div class="text-right mt-6 sm:mt-0">
            <p class="text-5xl font-semibold mb-2">
              {{ currencyFormater.format(props.userQuotePrice.price.price) }}
            </p>
            <p class="uppercase font-semibold" v-if="question_answer(outTheDoorQuestionId)?.value === 'Yes'">
              {{ t('my_garage.quotes.out_the_door') }}
            </p>
            <p class="uppercase font-semibold" v-if="question_answer(outTheDoorQuestionId)?.value === 'No'">
              {{ t('my_garage.quotes.tax_not_included') }}
            </p>

            <p class="uppercase font-semibold" v-if="question_answer(oemPartsQuestionId)?.value === 'Yes'">
              {{ t('my_garage.quotes.oem_parts') }}
            </p>

            <p>
              {{ props.userQuotePrice.price.shopperNote }}
            </p>
          </div>

          <div class="grow"></div>

          <button class="rounded-full bg-[#b05b34] text-white py-2 md:py-3 px-8 whitespace-nowrap">
          <span class="pr-4 focus:outline-none text-base md:text-lg font-normal whitespace-nowrap">
            {{ t('my_garage.quotes.call_number', { phoneNumber: props.userQuotePrice.service_provider.formatted_phone_number }) }}
          </span>
            <FontAwesomeIcon :icon="faPhone" class="inline" size="lg" />
          </button>
        </div>

      </div>
    </div>
    <div class="flex flex-row mb-6 mx-8">
      <p class="text-gray-500">
        {{ t('my_garage.quotes.disclaimer_blurb') }}
      </p>
    </div>

  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styles/views/my_garage/quote';
</style>
